import { ActionIcon, Collapse, Group, Paper, Text, UnstyledButton } from "@mantine/core";
import { IconChevronDown, IconChevronUp, IconX } from "@tabler/icons-react";
import {
  createContext, type CSSProperties, type ReactNode, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from "react";

import { useT } from "../i18n/script";

/**
 * Kolona kartica preko karte (lijevo ili desno). Kartice se slažu jedna ispod druge i nikad se ne preklapaju.
 * Kad nema mjesta, kolona sama sklopi najstariju otvorenu karticu (ostaje samo naslov) — nova ili upravo otvorena
 * kartica ostaje otvorena; klik na naslov kartice je otvara ili sklapa. Bez klizača ako za njim nema potrebe.
 */
interface DockCtx {
  isCollapsed: (id: string) => boolean;
  toggle: (id: string) => void;
  register: (id: string, collapsed?: boolean) => () => void;
}
const Ctx = createContext<DockCtx | null>(null);

export function MapDock({ side, children }: { side: "left" | "right"; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const active = useRef<Record<string, number>>({});
  const timer = useRef<number>(0);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el || el.scrollHeight <= el.clientHeight + 2) return;
    const open = Object.keys(active.current).filter((id) => !collapsedRef.current[id]);
    if (open.length < 2) return; // jedina otvorena kartica ostaje otvorena (ima svoj klizač ako je predugačka)
    open.sort((a, b) => active.current[a] - active.current[b]);
    setCollapsed((c) => ({ ...c, [open[0]]: true }));
  }, []);
  const collapsedRef = useRef(collapsed);
  collapsedRef.current = collapsed;
  const schedule = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(fit, 230); // poslije animacije sklapanja
  }, [fit]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    const mo = new MutationObserver(() => {
      el.querySelectorAll(":scope > *").forEach((c) => ro.observe(c));
      schedule();
    });
    mo.observe(el, { childList: true, subtree: true });
    el.querySelectorAll(":scope > *").forEach((c) => ro.observe(c));
    return () => { ro.disconnect(); mo.disconnect(); window.clearTimeout(timer.current); };
  }, [schedule]);

  const register = useCallback((id: string, start?: boolean) => {
    active.current[id] = Date.now();
    if (start) setCollapsed((c) => ({ ...c, [id]: true }));
    schedule();
    return () => {
      delete active.current[id];
      setCollapsed((c) => { const n = { ...c }; delete n[id]; return n; });
    };
  }, [schedule]);
  const value = useMemo<DockCtx>(() => ({
    isCollapsed: (id) => !!collapsed[id],
    toggle: (id) => {
      active.current[id] = Date.now();
      setCollapsed((c) => ({ ...c, [id]: !c[id] }));
      schedule();
    },
    register,
  }), [collapsed, schedule, register]);

  return (
    <Ctx.Provider value={value}>
      <div ref={ref} className={`map-dock map-dock-${side}`}>{children}</div>
    </Ctx.Provider>
  );
}

/** Kartica u koloni: naslov (klik sklapa/otvara), opciono zatvaranje, sadržaj. */
export function DockCard({ id, title, label, icon, badge, onClose, children, grow, startCollapsed, style }: {
  id: string; title: ReactNode; label?: ReactNode; icon?: ReactNode; badge?: ReactNode; onClose?: () => void; children: ReactNode;
  grow?: boolean; startCollapsed?: boolean; style?: CSSProperties;
}) {
  const { t } = useT();
  const dock = useContext(Ctx);
  const [localCollapsed, setLocal] = useState(!!startCollapsed);
  const reg = dock?.register;
  useLayoutEffect(() => reg?.(id, startCollapsed), [reg, id, startCollapsed]);
  const collapsed = dock ? dock.isCollapsed(id) : localCollapsed;
  const toggle = () => (dock ? dock.toggle(id) : setLocal(!localCollapsed));
  return (
    <Paper shadow="md" radius="md" p="sm" className={`map-dock-item${grow && !collapsed ? " map-dock-grow" : ""}`} style={style}>
      <Group justify="space-between" wrap="nowrap" gap={6}>
        <UnstyledButton onClick={toggle} style={{ flex: 1, minWidth: 0 }} aria-expanded={!collapsed}
          title={t(collapsed ? "Otvori" : "Sklopi")}>
          <Group gap={6} wrap="nowrap">
            {icon}
            <div style={{ minWidth: 0 }}>
              {label && <Text size="10px" c="dimmed" lh={1.1}>{label}</Text>}
              <Text fw={650} size="sm" lh={1.25} truncate>{title}</Text>
            </div>
            {badge}
            {collapsed ? <IconChevronDown size={14} style={{ flexShrink: 0, opacity: 0.7 }} /> : <IconChevronUp size={14} style={{ flexShrink: 0, opacity: 0.7 }} />}
          </Group>
        </UnstyledButton>
        {onClose && <ActionIcon size="sm" variant="subtle" color="gray" onClick={onClose} aria-label={t("Zatvori")}><IconX size={14} /></ActionIcon>}
      </Group>
      <Collapse expanded={!collapsed} transitionDuration={180} className={grow ? "map-dock-body" : undefined}>
        <div className="map-dock-content">{children}</div>
      </Collapse>
    </Paper>
  );
}
